// F08 reports (decision D22 slice D; RF-OBS-16, AC-52, AC-59): bottlenecks before/after two windows and conventional
// review vs Gauntlet per task class. Measured facts with sample and completeness, the fixed interpretation note, and no
// recommendation or automatic change.
import { useState } from "react";
import { useI18n } from "../../i18n/index.tsx";
import { getBottleneckReport, getReviewModeReport } from "../../api/endpoints-wave2.ts";
import type { BottleneckReport, ReviewModeReport, WindowQualityCost } from "../../api/types-wave2.ts";
import { useAsync } from "../../lib/useAsync.ts";
import { formatCents, formatMs } from "../../lib/format.ts";
import { SelectField } from "../../components/Field.tsx";
import { ErrorBanner, LoadingBlock } from "../../components/Feedback.tsx";

const DAY = 86_400_000;
const iso = (msAgo: number) => new Date(Date.now() - msAgo).toISOString();
const pct = (v: number | null) => (v === null ? "—" : `${Math.round(v * 100)}%`);

export function ReportsCard({ projectId }: { projectId: string }) {
  const { t } = useI18n();
  const [groupBy, setGroupBy] = useState<"step" | "model" | "role">("step");
  const bottlenecks = useAsync(
    (s) => getBottleneckReport(projectId, { before_from: iso(30 * DAY), before_to: iso(15 * DAY), after_from: iso(15 * DAY), after_to: iso(0), group_by: groupBy }, s),
    [projectId, groupBy],
  );
  const reviews = useAsync((s) => getReviewModeReport(projectId, { from: iso(30 * DAY), to: iso(0) }, s), [projectId]);
  return (
    <section className="card" aria-labelledby="reports-title" data-testid="reports-card">
      <div className="card-header">
        <h2 id="reports-title" className="card-title" style={{ flex: 1 }}>
          {t("reports.title")}
        </h2>
      </div>
      <div className="card-body stack">
        <div className="row row--wrap">
          <h3 className="subheading" style={{ flex: 1 }}>
            {t("reports.bottlenecks")}
          </h3>
          <SelectField label={t("reports.groupBy")} value={groupBy} onChange={(e) => setGroupBy(e.target.value as "step" | "model" | "role")}>
            <option value="step">{t("reports.group.step")}</option>
            <option value="model">{t("reports.group.model")}</option>
            <option value="role">{t("reports.group.role")}</option>
          </SelectField>
        </div>
        {bottlenecks.loading ? <LoadingBlock label={t("common.loading")} lines={2} /> : bottlenecks.error ? <ErrorBanner error={bottlenecks.error} onRetry={bottlenecks.reload} /> : bottlenecks.data ? <BottleneckTable report={bottlenecks.data} /> : null}
        <h3 className="subheading">{t("reports.reviewModes")}</h3>
        {reviews.loading ? <LoadingBlock label={t("common.loading")} lines={2} /> : reviews.error ? <ErrorBanner error={reviews.error} onRetry={reviews.reload} /> : reviews.data ? <ReviewTable report={reviews.data} /> : null}
      </div>
    </section>
  );
}

function BottleneckTable({ report }: { report: BottleneckReport }) {
  const { t, locale } = useI18n();
  // AC-59: quality and cost of the window; unknown costs are shown apart, never as zero.
  const qualityCostLine = (w: WindowQualityCost) =>
    t("reports.qualityCost", {
      succeeded: w.succeeded,
      attempts: w.attempts,
      success: pct(w.success_rate),
      passed: w.gates_passed,
      gates: w.gates_passed + w.gates_failed,
      passRate: pct(w.gate_pass_rate),
      cost: formatCents(w.known_cost_cents, locale),
      unknown: w.unknown_cost_calls,
    });
  return (
    <div className="stack" style={{ gap: 6 }} data-testid="bottleneck-report">
      {report.groups.length === 0 ? (
        <p className="subtle">{t("reports.noSpans")}</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <caption className="sr-only">{t("reports.bottlenecks")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("reports.group")}</th>
                <th scope="col">{t("reports.before")}</th>
                <th scope="col">{t("reports.after")}</th>
                <th scope="col">{t("reports.delta")}</th>
              </tr>
            </thead>
            <tbody>
              {report.groups.map((g) => (
                <tr key={g.group} data-testid="bottleneck-row" data-comparable={String(g.comparable)}>
                  <th scope="row" className="mono">
                    {g.group}
                  </th>
                  {[g.before, g.after].map((w, i) => (
                    <td key={i}>{w ? `p50 ${w.p50_ms === null ? "—" : formatMs(w.p50_ms)} · p95 ${w.p95_ms === null ? "—" : formatMs(w.p95_ms)} · n=${w.sample} (${pct(w.completeness)})` : "—"}</td>
                  ))}
                  <td>{g.comparable && g.p50_delta_ms !== null ? `${g.p50_delta_ms > 0 ? "+" : ""}${formatMs(g.p50_delta_ms)}` : <span className="subtle">{t("reports.notComparable")}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {report.quality_cost ? (
        <ul className="field-hint" data-testid="bottleneck-quality-cost">
          <li>{t("reports.before")}: {qualityCostLine(report.quality_cost.before)}</li>
          <li>{t("reports.after")}: {qualityCostLine(report.quality_cost.after)}</li>
        </ul>
      ) : null}
      <p className="field-hint">{t(report.conditions.same_models && report.conditions.same_task_mix ? "reports.sameConditions" : "reports.differentConditions")}</p>
      <p className="field-hint" data-testid="bottleneck-note">
        {report.note}
      </p>
    </div>
  );
}

function ReviewTable({ report }: { report: ReviewModeReport }) {
  const { t } = useI18n();
  const comparable = new Map(report.classes.map((c) => [c.task_class, c]));
  return (
    <div className="stack" style={{ gap: 6 }} data-testid="review-mode-report">
      {report.rows.length === 0 ? (
        <p className="subtle">{t("reports.noReviews")}</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <caption className="sr-only">{t("reports.reviewModes")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("reports.taskClass")}</th>
                <th scope="col">{t("reports.mode")}</th>
                <th scope="col">{t("reports.tasks")}</th>
                <th scope="col">{t("reports.firstRound")}</th>
                <th scope="col">{t("reports.rounds")}</th>
                <th scope="col">{t("reports.rework")}</th>
                <th scope="col">{t("reports.costPerAccepted")}</th>
              </tr>
            </thead>
            <tbody>
              {report.rows.map((r) => (
                <tr key={`${r.task_class}:${r.mode}`} data-testid="review-mode-row">
                  <th scope="row">
                    <span className="mono">{r.task_class}</span>
                    {comparable.get(r.task_class)?.comparable === false ? <div className="subtle">{comparable.get(r.task_class)?.reason}</div> : null}
                  </th>
                  <td>{r.mode === "gauntlet" ? "Gauntlet" : t("reports.conventional")}</td>
                  <td>
                    {r.accepted}/{r.tasks}
                  </td>
                  <td>{pct(r.first_round_acceptance)}</td>
                  <td>{r.avg_review_rounds === null ? "—" : r.avg_review_rounds.toFixed(1)}</td>
                  <td>{r.rework_attempts}</td>
                  <td>{r.cost.per_accepted_cents === null ? <span className="subtle">{t("reports.costUnknown")}</span> : `${(r.cost.per_accepted_cents / 100).toFixed(2)}`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="field-hint" data-testid="review-mode-note">
        {report.note}
      </p>
    </div>
  );
}
